import React, { useState, useEffect, useRef } from "react";
import {
  Form,
  Input,
  Button,
  Upload,
  message,
  Spin,
  Card,
  Space,
  Divider,
  Row,
  Col,
  Switch,
  Select,
} from "antd";
import { UploadOutlined } from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import api from "../../api";
import TinyEditor from "../../components/Editor";
import { SEO_CONFIG } from "../../seo-congig";

const { TextArea } = Input;

const EditBrand = () => {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [loadingBrand, setLoadingBrand] = useState(false);
  const [uploading, setUploading] = useState(false);
  const editorRef = useRef(null);
  const [editorContent, setEditorContent] = useState("");
  const [existingBanner, setExistingBanner] = useState(null);
  const [countries, setCountries] = useState([]);
  const [fetchingCountries, setFetchingCountries] = useState(false);

  useEffect(() => {
    const fetchCountries = async () => {
      setFetchingCountries(true);
      try {
        const response = await api.get("/panel/country");
        setCountries(response.data.data);
      } catch (error) {
        message.error("خطا در دریافت لیست کشورها");
      } finally {
        setFetchingCountries(false);
      }
    };
    fetchCountries();
  }, []);

  const handleEditorChange = (content) => {
    setEditorContent(content);
  };

  const brandTypeOptions = [
    { value: "car", label: "خودرو" },
    { value: "part", label: "قطعه" },
  ];

  useEffect(() => {
    const fetchBrand = async () => {
      setLoadingBrand(true);
      try {
        const response = await api.get(`/panel/brand/${id}`);
        const data = response?.data?.data;
        
        // Extract country IDs from the countries array
        const countryIds = data?.countries?.map(country => country.id) || [];

        form.setFieldsValue({
          name: data?.name,
          countries: countryIds, // Set multiple countries
          slug: data?.slug,
          type: data?.type?.toString(),
          seo_title: data?.seo_title,
          seo_description: data?.seo_description,
          canonical: data?.canonical,
          follow: data?.follow,
          index: data?.index,
        });
        setEditorContent(data.body || "");
        setExistingBanner(data.image);
      } catch (error) {
        message.error("خطا در دریافت اطلاعات برند.");
        navigate("/brands");
      } finally {
        setLoadingBrand(false);
      }
    };

    fetchBrand();
  }, [id, form, navigate]);

  const onFinish = async (values) => {
    setLoading(true);
    try {
      const formData = new FormData();

      const appendIfExists = (key, value) => {
        if (value !== undefined && value !== null && value !== "") {
          formData.append(key, value);
        }
      };

      // Append country IDs as separate entries
      if (values.countries && values.countries.length > 0) {
        values.countries.forEach((countryId, index) => {
          formData.append(`countries[${index}]`, countryId);
        });
      }

      formData.append("name", values.name);
      formData.append("body", editorContent);
      formData.append("slug", values.slug);
      formData.append("type", values.type);

      // SEO Fields (ensure empty values are sent)
      appendIfExists("seo_title", values.seo_title);
      appendIfExists("seo_description", values.seo_description);
      appendIfExists("canonical", values.canonical);
      formData.append("follow", values.follow ? "1" : "0");
      formData.append("index", values.index ? "1" : "0");

      if (values.banner?.file) {
        formData.append("image", values.banner.file);
      }

      await api.post(`/panel/brand/${id}?_method=PUT`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      message.success("برند با موفقیت به‌روزرسانی شد.");
      navigate("/brands");
    } catch (error) {
      message.error("خطا در به‌روزرسانی برند.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <div>
        <h2 className="mb-10 text-xl">ویرایش برند</h2>
        <Spin spinning={loading || loadingBrand}>
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
          >
            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="name"
                  label="عنوان برند"
                  rules={[
                    {
                      required: true,
                      message: "لطفاً عنوان را وارد کنید.",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item name="countries" label="کشورها">
                  <Select
                    placeholder="کشورها را انتخاب کنید"
                    loading={fetchingCountries}
                    showSearch
                    mode="multiple" // Enable multiple selection
                    optionFilterProp="children"
                    filterOption={(input, option) =>
                      option.children
                        .toLowerCase()
                        .includes(input.toLowerCase())
                    }
                  >
                    {countries.map((country) => (
                      <Select.Option key={country.id} value={country.id}>
                        {country.name}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item
                  name="slug"
                  label="پیوند(لینک این  برند در وبسایت)"
                  rules={[
                    {
                      required: true,
                      message: "لطفاً پیوند را وارد کنید.",
                    },
                  ]}
                >
                  <Input />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="type" label="نوع  برند">
                  <Select placeholder="نوع  برند را انتخاب کنید">
                    {brandTypeOptions.map((option) => (
                      <Select.Option key={option.value} value={option.value}>
                        {option.label}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            </Row>

            <Form.Item
              label="محتوا"
              required
              validateTrigger={["onChange", "onBlur"]}
              rules={[
                {
                  required: true,
                  validator: (_, value) => {
                    if (!editorContent) {
                      return Promise.reject("لطفاً محتوا را وارد کنید.");
                    }
                    return Promise.resolve();
                  },
                },
              ]}
            >
              <TinyEditor
                content={editorContent}
                onEditorChange={handleEditorChange}
                model={"Brand"}
                height={300}
              />
            </Form.Item>

            <div className="border p-4 rounded-lg mb-4">
              <h3 className="text-xl text-blue-500 text-center mb-4">رسانه</h3>

              <Form.Item
                name="banner"
                label="تصویر بنر"
                extra={
                  existingBanner && (
                    <img
                      className="mt-4"
                      src={existingBanner}
                      alt="بنر موجود"
                      style={{ maxWidth: "200px" }}
                    />
                  )
                }
              >
                <Upload
                  maxCount={1}
                  beforeUpload={() => false}
                  listType="picture"
                >
                  <Button icon={<UploadOutlined />} loading={uploading}>
                    {existingBanner ? "تغییر تصویر" : "انتخاب تصویر"}
                  </Button>
                </Upload>
              </Form.Item>
            </div>

            <Divider>بخش سئو</Divider>
            <Form.Item
              name="seo_title"
              label="عنوان سئو"
              help={SEO_CONFIG.title.helpText}
            >
              <Input maxLength={SEO_CONFIG.title.maxLength} showCount />
            </Form.Item>

            <Form.Item
              name="seo_description"
              label="توضیحات سئو"
              help={SEO_CONFIG.description.helpText}
            >
              <TextArea
                rows={4}
                maxLength={SEO_CONFIG.description.maxLength}
                showCount
              />
            </Form.Item>

            <Form.Item name="canonical" label="URL Canonical">
              <Input />
            </Form.Item>

            <Row gutter={24}>
              <Col span={12}>
                <Form.Item name="follow" label="Follow" valuePropName="checked">
                  <Switch defaultChecked />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="index" label="Index" valuePropName="checked">
                  <Switch defaultChecked />
                </Form.Item>
              </Col>
            </Row>

            <Form.Item>
              <Space>
                <Button type="primary" htmlType="submit" loading={loading}>
                  ذخیره تغییرات
                </Button>
                <Button
                  onClick={() => navigate("/brands")}
                  style={{ marginRight: "8px" }}
                >
                  انصراف
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Spin>
      </div>
    </Card>
  );
};

export default EditBrand;